import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShieldCheck, Download, Infinity, CreditCard, ChevronDown, Star, ArrowRight, AlertTriangle, BookOpen, Users, TrendingUp, Globe, Briefcase, FileText, HelpCircle, Lock, Zap, FileDown } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { SocialProofPopup } from './SocialProofPopup';
// Image URLs
const IMAGES = {
  MAIN: 'https://cdn.magicpatterns.com/uploads/2wznTXxMZ2e2Xpf6WUnirQ/mockups_economia_.png',
  BONUS1: 'https://cdn.magicpatterns.com/uploads/6JxosyVSZjXjLscaapUHyG/bonus_maletin_de_act.png',
  BONUS2: 'https://cdn.magicpatterns.com/uploads/xdCZarQDHvWmR8sHQbcS62/bonus_primeros_auxilios.png',
  BONUS3: 'https://cdn.magicpatterns.com/uploads/7jPddC3JCCs6g7bjXSoGJ3/bonus_planificacion_anual.png',
  UPSELL: 'https://cdn.magicpatterns.com/uploads/9J3MioKiq1fVGdgGA4X9rp/upsell_economia_.png'
};
// Links
const LINKS = {
  MAIN_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554509390144:1',
  UPSELL_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51553895448896:1',
  CONTABILIDAD_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554510143808:1',
  PREMIUM_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51554510471488:1',
  PROYECTO_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51622247465280:1',
  ECO_TEORIA_CHECKOUT: 'https://mi-tienda-2-8952.myshopify.com/cart/51622301204800:1'
};
export function SalesPage() {
  const [openCuadernillo, setOpenCuadernillo] = useState<number | null>(null);
  const [selectedPack, setSelectedPack] = useState(1); // 0=economia, 1=eco+gestion, 2=contabilidad, 3=premium
  const packLinks = [LINKS.MAIN_CHECKOUT, LINKS.UPSELL_CHECKOUT, LINKS.CONTABILIDAD_CHECKOUT, LINKS.PREMIUM_CHECKOUT, LINKS.PROYECTO_CHECKOUT, LINKS.ECO_TEORIA_CHECKOUT];
  // Urgency countdown (2h 13m 30s from page load)
  const [urgencyTime, setUrgencyTime] = useState({
    hours: 2,
    minutes: 13,
    seconds: 30
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgencyTime(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        seconds--;
        if (seconds < 0) {
          seconds = 59;
          minutes--;
        }
        if (minutes < 0) {
          minutes = 59;
          hours--;
        }
        if (hours < 0) {
          hours = 0;
          minutes = 0;
          seconds = 0;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="font-sans text-gray-900 bg-white min-h-screen flex flex-col" data-id="element-2">
      <CountdownTimer data-id="element-3" />
      <SocialProofPopup data-id="element-4" />

      {/* SECTION 2: HERO */}
      <section className="bg-black py-4 md:py-16 px-3 md:px-4 text-center" data-id="element-5">
        <div className="max-w-4xl mx-auto" data-id="element-6">
          <span className="inline-block border border-[#22c55e] text-[#22c55e] text-[10px] md:text-sm font-bold uppercase tracking-widest mb-2 md:mb-5 px-3 py-1 rounded-full whitespace-nowrap" data-id="element-7">
            Para docentes de Economía en secundaria
          </span>

          <h1 className="text-2xl md:text-5xl lg:text-6xl font-black text-white mb-1 md:mb-2 leading-tight" data-id="element-8">
            Resolvé todo tu año de Economía en un solo lugar y olvidate de
            planificar desde cero con el{' '}
            <span className="text-[#22c55e]" data-id="element-9">KIT COMPLETO ECONOMÍA LISTA</span>
          </h1>

          <div onClick={scrollToPricing} className="cursor-pointer hover:opacity-95 transition-opacity my-2 md:my-8" data-id="element-10">
            <img src={IMAGES.MAIN} alt="Kit Completo Economía Lista Mockup" className="w-full max-w-lg mx-auto drop-shadow-2xl" data-id="element-11" />
          </div>

          <p className="text-base md:text-2xl text-[#22c55e] font-bold italic mb-1 md:mb-3" data-id="element-12">
            "Listo para usar. Sin buscar más."
          </p>

          <p className="text-sm md:text-lg text-gray-300 mb-2 md:mb-6 max-w-2xl mx-auto" data-id="element-13">
            Sistema completo de clases, secuencias, actividades y evaluaciones
            listas para usar en secundaria. Todo tu año resuelto.
          </p>

          {/* Small badges above CTA */}
          <div className="flex justify-center gap-1.5 md:gap-3 mb-2 md:mb-4 flex-nowrap" data-id="element-14">
            <span className="flex items-center gap-1 border border-gray-600 text-gray-300 text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full whitespace-nowrap" data-id="element-15">
              <FileText className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-16" /> 100% PDF
            </span>
            <span className="flex items-center gap-1 border border-gray-600 text-gray-300 text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full whitespace-nowrap" data-id="element-17">
              <Zap className="w-2.5 h-2.5 md:w-3 md:h-3" data-id="element-18" /> Acceso inmediato
            </span>
            <span className="flex items-center gap-1 border border-gray-600 text-gray-300 text-[10px] md:text-xs px-2 py-0.5 md:px-3 md:py-1 rounded-full whitespace-nowrap" data-id="element-19">
              🇦🇷 Currículo argentino
            </span>
          </div>

          <button onClick={scrollToPricing} className="inline-flex items-center justify-center bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-sm md:text-xl py-2.5 px-5 md:py-3 md:px-8 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] transform hover:scale-105 transition-all duration-200 mb-2 md:mb-4 whitespace-nowrap" data-id="element-20">
            → QUIERO MI KIT COMPLETO AHORA
          </button>

          {/* Trust icons below CTA */}
          <div className="flex justify-center gap-2 md:gap-4 text-[10px] md:text-xs text-gray-500 font-medium flex-nowrap" data-id="element-21">
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-22">
              <ShieldCheck className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-23" />{' '}
              Garantía 7 días
            </span>
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-24">
              <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-25" /> Pago
              seguro
            </span>
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-26">
              <CreditCard className="w-3 h-3 md:w-3.5 md:h-3.5 text-green-500" data-id="element-27" />{' '}
              Mercado Pago
            </span>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BAR */}
      <section className="bg-black pb-6 md:pb-10 px-4" data-id="element-28">
        <div className="flex flex-col items-center justify-center gap-2" data-id="element-29">
          {/* Overlapping avatars */}
          <div className="flex items-center" data-id="element-30">
            <div className="flex -space-x-2.5" data-id="element-31">
              {['https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/68.jpg', 'https://randomuser.me/api/portraits/men/75.jpg', 'https://randomuser.me/api/portraits/women/65.jpg', 'https://randomuser.me/api/portraits/women/47.jpg'].map((src, i) => <img key={i} src={src} alt="" className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-black object-cover" data-id="element-32" />)}
            </div>
            <div className="ml-3 flex flex-col" data-id="element-33">
              <div className="flex items-center gap-1" data-id="element-34">
                <div className="flex gap-0.5 text-yellow-400" data-id="element-35">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current" data-id="element-36" />)}
                </div>
                <span className="text-white font-bold text-xs md:text-sm" data-id="element-37">
                  4.9
                </span>
              </div>
              <p className="text-gray-400 text-[11px] md:text-xs" data-id="element-38">
                <span className="text-white font-bold" data-id="element-39">+1.800 docentes</span> ya
                lo usan en toda Argentina
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: PAIN POINTS */}
      <section className="bg-gray-50 py-6 md:py-16 px-4 border-t border-gray-200" data-id="element-40">
        <div className="max-w-3xl mx-auto" data-id="element-41">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4 md:mb-8" data-id="element-42">
            Si sos docente de Economía, esto te va a sonar{' '}
            <span className="text-red-500 underline decoration-red-500/30 decoration-4 underline-offset-4" data-id="element-43">
              familiar…
            </span>
          </h2>

          <div className="space-y-3 md:space-y-6 text-base md:text-xl text-gray-700 leading-relaxed" data-id="element-44">
            <p data-id="element-45">Llegás del colegio agotado/a y todavía tenés que planificar.</p>
            <p data-id="element-46">
              Horas armando clases sobre oferta y demanda, inflación, crisis
              económicas, sistema financiero, desarrollo, mercado laboral…
            </p>
            <p data-id="element-47">
              Preparando evaluaciones claras. Buscando actividades que realmente
              enganchen. Tratando de que los contenidos no queden solo en
              teoría.
            </p>
            <p className="font-bold text-gray-900" data-id="element-48">
              No es falta de capacidad. Es que te falta un sistema completo que
              te sostenga todo el año.
            </p>
          </div>
        </div>
      </section>

      {/* EXTRA STRATEGIC CTA */}
      <section className="bg-white py-4 md:py-8 px-4 text-center" data-id="element-49">
        <button onClick={scrollToPricing} className="bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold text-lg md:text-2xl py-4 px-8 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 w-full md:w-auto max-w-2xl" data-id="element-50">
          🔥 Quiero dejar de improvisar y tener todo resuelto
        </button>
      </section>

      {/* SECTION 4: SOLUTION */}
      <section className="bg-black text-white py-8 md:py-20 px-4 text-center" data-id="element-51">
        <div className="max-w-4xl mx-auto" data-id="element-52">
          <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 md:mb-6 uppercase tracking-wider" data-id="element-53">
            La Solución
          </span>
          <h2 className="text-4xl md:text-6xl font-black mb-3 md:mb-6" data-id="element-54">
            KIT COMPLETO{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-8" data-id="element-55">
              ECONOMÍA LISTA
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-5 md:mb-10 max-w-3xl mx-auto" data-id="element-56">
            Sistema integral de clases, actividades, proyectos y evaluaciones
            diseñadas específicamente para secundaria.
          </p>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-lg md:text-xl font-bold text-[#22c55e]" data-id="element-57">
            <span className="flex items-center gap-2" data-id="element-58">
              <Check className="w-6 h-6" data-id="element-59" /> Listo
            </span>
            <span className="flex items-center gap-2" data-id="element-60">
              <Check className="w-6 h-6" data-id="element-61" /> Probado
            </span>
            <span className="flex items-center gap-2" data-id="element-62">
              <Check className="w-6 h-6" data-id="element-63" /> Organizado
            </span>
            <span className="flex items-center gap-2" data-id="element-64">
              <Check className="w-6 h-6" data-id="element-65" /> Aplicable
            </span>
          </div>
        </div>
      </section>

      {/* SECTION 5: CONTENTS */}
      <section className="bg-white py-8 md:py-20 px-4" data-id="element-66">
        <div className="max-w-4xl mx-auto" data-id="element-67">
          <div className="text-center mb-6 md:mb-12" data-id="element-68">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-69">
              ¿QUÉ INCLUYE EL{' '}
              <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-4" data-id="element-70">
                KIT COMPLETO
              </span>{' '}
              ECONOMÍA LISTA?
            </h2>
            <p className="text-xl text-gray-600" data-id="element-71">
              ✅ Tocá cada cuadernillo para ver su contenido
            </p>
          </div>

          <div className="space-y-2 md:space-y-4" data-id="element-72">
            {/* Cuadernillo 1 */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-73">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 0 ? null : 0)} className="w-full flex items-center gap-4 p-4 md:p-8 text-left cursor-pointer" data-id="element-74">
                <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" data-id="element-75" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-76">
                  Cuadernillo Economía 1 – Introducción a la Economía
                </h3>
                <ChevronDown className={`w-6 h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 0 ? 'rotate-180' : ''}`} data-id="element-77" />
              </button>
              <AnimatePresence data-id="element-78">
                {openCuadernillo === 0 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-79">
                    <div className="px-4 md:px-8 pb-4 md:pb-8 border-t border-gray-200 pt-4" data-id="element-80">
                      <p className="text-gray-600 italic mb-4" data-id="element-81">
                        Bases conceptuales, microeconomía inicial y mirada
                        cotidiana.
                      </p>
                      <ul className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-82">
                        <li className="flex items-start gap-2" data-id="element-83">
                          📌 Qué es la economía y su vínculo con la vida
                          cotidiana
                        </li>
                        <li className="flex items-start gap-2" data-id="element-84">
                          📌 Necesidades, recursos y escasez
                        </li>
                        <li className="flex items-start gap-2" data-id="element-85">
                          📌 Bienes y servicios
                        </li>
                        <li className="flex items-start gap-2" data-id="element-86">
                          📌 Producción, distribución y consumo
                        </li>
                        <li className="flex items-start gap-2" data-id="element-87">
                          📌 Decisiones económicas individuales (base de la
                          microeconomía)
                        </li>
                        <li className="flex items-start gap-2" data-id="element-88">
                          📌 Organización social y economía
                        </li>
                        <li className="flex items-start gap-2" data-id="element-89">
                          📌 Desarrollo conceptual claro y ordenado
                        </li>
                        <li className="flex items-start gap-2" data-id="element-90">
                          📌 Bibliografía y material de apoyo
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Cuadernillo 2 */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-91">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 1 ? null : 1)} className="w-full flex items-center gap-4 p-4 md:p-8 text-left cursor-pointer" data-id="element-92">
                <TrendingUp className="w-8 h-8 text-green-600 flex-shrink-0" data-id="element-93" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-94">
                  Cuadernillo Economía 2 – Sistemas económicos, Estado y
                  Macroeconomía
                </h3>
                <ChevronDown className={`w-6 h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 1 ? 'rotate-180' : ''}`} data-id="element-95" />
              </button>
              <AnimatePresence data-id="element-96">
                {openCuadernillo === 1 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-97">
                    <div className="px-4 md:px-8 pb-4 md:pb-8 border-t border-gray-200 pt-4" data-id="element-98">
                      <p className="text-gray-600 italic mb-4" data-id="element-99">
                        Organización económica, rol del Estado y mirada
                        macroeconómica.
                      </p>
                      <ul className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-100">
                        <li className="flex items-start gap-2" data-id="element-101">
                          🏛️ Qué es un sistema económico
                        </li>
                        <li className="flex items-start gap-2" data-id="element-102">
                          🏛️ Economía de mercado, planificada y mixta
                        </li>
                        <li className="flex items-start gap-2" data-id="element-103">
                          🏛️ Microeconomía y Macroeconomía: diferencias y
                          relaciones
                        </li>
                        <li className="flex items-start gap-2" data-id="element-104">
                          🏛️ El Estado como actor económico
                        </li>
                        <li className="flex items-start gap-2" data-id="element-105">
                          🏛️ Impuestos, gasto público y servicios públicos
                        </li>
                        <li className="flex items-start gap-2" data-id="element-106">
                          🏛️ Estado, mercado y sociedad
                        </li>
                        <li className="flex items-start gap-2" data-id="element-107">
                          🏛️ Contenidos explicados sin tecnicismos
                        </li>
                        <li className="flex items-start gap-2" data-id="element-108">
                          🏛️ Bibliografía y recursos didácticos
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Cuadernillo 3 */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-109">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 2 ? null : 2)} className="w-full flex items-center gap-4 p-4 md:p-8 text-left cursor-pointer" data-id="element-110">
                <Users className="w-8 h-8 text-purple-600 flex-shrink-0" data-id="element-111" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-112">
                  Cuadernillo Economía 3 – Trabajo, ingresos y desigualdad
                </h3>
                <ChevronDown className={`w-6 h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 2 ? 'rotate-180' : ''}`} data-id="element-113" />
              </button>
              <AnimatePresence data-id="element-114">
                {openCuadernillo === 2 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-115">
                    <div className="px-4 md:px-8 pb-4 md:pb-8 border-t border-gray-200 pt-4" data-id="element-116">
                      <p className="text-gray-600 italic mb-4" data-id="element-117">
                        Micro y macro desde la dimensión social de la economía.
                      </p>
                      <ul className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-118">
                        <li className="flex items-start gap-2" data-id="element-119">
                          👥 Trabajo y empleo
                        </li>
                        <li className="flex items-start gap-2" data-id="element-120">
                          👥 Ingresos y distribución del ingreso
                        </li>
                        <li className="flex items-start gap-2" data-id="element-121">
                          👥 Microeconomía del trabajo y los ingresos
                        </li>
                        <li className="flex items-start gap-2" data-id="element-122">
                          👥 Desigualdad social y económica
                        </li>
                        <li className="flex items-start gap-2" data-id="element-123">
                          👥 Economía, derechos sociales y ciudadanía
                        </li>
                        <li className="flex items-start gap-2" data-id="element-124">
                          👥 Impacto de la economía en la vida cotidiana
                        </li>
                        <li className="flex items-start gap-2" data-id="element-125">
                          👥 Enfoque social y ciudadano
                        </li>
                        <li className="flex items-start gap-2" data-id="element-126">
                          👥 Bibliografía y actividades sugeridas
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Cuadernillo 4 */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden transition-shadow hover:shadow-md" data-id="element-127">
              <button onClick={() => setOpenCuadernillo(openCuadernillo === 3 ? null : 3)} className="w-full flex items-center gap-4 p-4 md:p-8 text-left cursor-pointer" data-id="element-128">
                <Globe className="w-8 h-8 text-orange-600 flex-shrink-0" data-id="element-129" />
                <h3 className="text-lg md:text-xl font-bold text-gray-900 flex-1" data-id="element-130">
                  Cuadernillo Economía 4 – Economía argentina y mundo actual
                </h3>
                <ChevronDown className={`w-6 h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openCuadernillo === 3 ? 'rotate-180' : ''}`} data-id="element-131" />
              </button>
              <AnimatePresence data-id="element-132">
                {openCuadernillo === 3 && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-133">
                    <div className="px-4 md:px-8 pb-4 md:pb-8 border-t border-gray-200 pt-4" data-id="element-134">
                      <p className="text-gray-600 italic mb-4" data-id="element-135">
                        Macroeconomía aplicada al contexto nacional y global.
                      </p>
                      <ul className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base" data-id="element-136">
                        <li className="flex items-start gap-2" data-id="element-137">
                          🇦🇷 Estructura productiva argentina
                        </li>
                        <li className="flex items-start gap-2" data-id="element-138">
                          🇦🇷 Actividades económicas
                        </li>
                        <li className="flex items-start gap-2" data-id="element-139">
                          🇦🇷 Estado y economía nacional
                        </li>
                        <li className="flex items-start gap-2" data-id="element-140">
                          🇦🇷 Macroeconomía argentina
                        </li>
                        <li className="flex items-start gap-2" data-id="element-141">
                          🇦🇷 Economía argentina en el contexto mundial
                        </li>
                        <li className="flex items-start gap-2" data-id="element-142">
                          🇦🇷 Problemas económicos actuales (enfoque conceptual)
                        </li>
                        <li className="flex items-start gap-2" data-id="element-143">
                          🇦🇷 Análisis claro y actualizado
                        </li>
                        <li className="flex items-start gap-2" data-id="element-144">
                          🇦🇷 Bibliografía y material de referencia
                        </li>
                      </ul>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: RESULTS */}

      {/* SECTION 7: SOCIAL PROOF BAR */}

      {/* SECTION 8: BONUSES */}
      <section className="py-8 md:py-20 px-4" style={{
      background: 'linear-gradient(180deg, #080c1a 0%, #0d1229 100%)'
    }} data-id="element-145">
        <div className="max-w-5xl mx-auto" data-id="element-146">
          {/* Header */}
          <div className="text-center mb-6 md:mb-10" data-id="element-147">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-148">
              <span className="text-[#22c55e]" data-id="element-149">BONUS</span> exclusivos incluidos
              con tu compra
            </h2>
            <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto mb-5 md:mb-8" data-id="element-150">
              Para que tengas respaldo profesional, evaluaciones sólidas y
              listas de herramientas para implementar, incluimos 3 bonos
              estratégicos:
            </p>

            {/* Red CTA Banner */}
            <div className="inline-flex items-center gap-2 bg-red-600 text-white font-black text-base md:text-xl px-6 md:px-10 py-3 md:py-4 rounded-lg shadow-[0_0_30px_rgba(220,38,38,0.4)] mb-6 md:mb-10" data-id="element-151">
              <span className="text-xl md:text-2xl" data-id="element-152">🎁</span>
              ¡GRATIS POR EL DÍA DE HOY!
            </div>
          </div>

          {/* Bonus Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5 mb-5 md:mb-10" data-id="element-153">
            {/* Bonus 1 */}
            <div className="relative bg-[#111633] rounded-lg border border-[#1e2a5a] overflow-hidden p-3 md:p-5 flex flex-col" data-id="element-154">
              <span className="absolute top-1 right-2 text-[50px] md:text-[80px] font-black text-white/[0.04] leading-none select-none pointer-events-none" data-id="element-155">
                01
              </span>
              <span className="inline-block bg-red-600 text-white text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 self-start" data-id="element-156">
                BONO #01
              </span>
              <h3 className="text-lg md:text-xl font-bold text-white mb-1.5" data-id="element-157">
                🩺 Guía Docente de Primeros Auxilios
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-158">
                Explica de forma clara y práctica cómo actuar ante emergencias
                escolares: desmayos, golpes, cortes o crisis nerviosas.
              </p>
              <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/10" data-id="element-159">
                <span className="text-gray-500 text-sm line-through" data-id="element-160">
                  Valor $15.000
                </span>
                <span className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-161">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="relative bg-[#111633] rounded-lg border border-[#1e2a5a] overflow-hidden p-3 md:p-5 flex flex-col" data-id="element-162">
              <span className="absolute top-1 right-2 text-[50px] md:text-[80px] font-black text-white/[0.04] leading-none select-none pointer-events-none" data-id="element-163">
                02
              </span>
              <span className="inline-block bg-yellow-500 text-black text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 self-start" data-id="element-164">
                BONO #02
              </span>
              <h3 className="text-lg md:text-xl font-bold text-white mb-1.5" data-id="element-165">
                📘 Guía Maestra de Planificación Anual
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-166">
                Incluye listas de planificaciones, organizadas por temas y
                niveles. Todo listo para que organices tu año sin estrés.
              </p>
              <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/10" data-id="element-167">
                <span className="text-gray-500 text-sm line-through" data-id="element-168">
                  Valor $12.000
                </span>
                <span className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-169">
                  GRATIS
                </span>
              </div>
            </div>

            {/* Bonus 3 */}
            <div className="relative bg-[#111633] rounded-lg border border-[#1e2a5a] overflow-hidden p-3 md:p-5 flex flex-col" data-id="element-170">
              <span className="absolute top-1 right-2 text-[50px] md:text-[80px] font-black text-white/[0.04] leading-none select-none pointer-events-none" data-id="element-171">
                03
              </span>
              <span className="inline-block bg-red-600 text-white text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider mb-2 self-start" data-id="element-172">
                BONO #03
              </span>
              <h3 className="text-lg md:text-xl font-bold text-white mb-1.5" data-id="element-173">
                ✨ Actividades para tus clases listas
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-174">
                Clases listas. 50 listas de actividades para que pongas en
                práctica en el aula. Recurso práctico e inmediato.
              </p>
              <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/10" data-id="element-175">
                <span className="text-gray-500 text-sm line-through" data-id="element-176">
                  Valor $10.000
                </span>
                <span className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-177">
                  GRATIS
                </span>
              </div>
            </div>
          </div>

          {/* Total Value Summary Box */}
          <div className="max-w-md mx-auto bg-[#111633] border border-[#1e2a5a] rounded-2xl p-4 md:p-8 text-center" data-id="element-178">
            <p className="text-gray-400 text-base mb-2" data-id="element-179">
              Valor total de los 3 bonus:
            </p>
            <p className="text-white text-2xl md:text-3xl font-black line-through decoration-red-500 decoration-2 mb-2 md:mb-4" data-id="element-180">
              $37.000
            </p>
            <p className="text-gray-400 text-base mb-2" data-id="element-181">
              Hoy te los llevás por:
            </p>
            <p className="text-[#22c55e] text-4xl md:text-6xl font-black mb-2 md:mb-4" data-id="element-182">
              $0
            </p>
            <p className="text-[#22c55e] font-bold text-sm md:text-base" data-id="element-183">
              ✨ Incluidos GRATIS con tu compra de hoy ✨
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 8.5: URGENCY */}
      <section className="py-8 md:py-20 px-4" style={{
      background: 'linear-gradient(180deg, #0d1229 0%, #0a0e1a 100%)'
    }} data-id="element-184">
        <div className="max-w-3xl mx-auto text-center" data-id="element-185">
          {/* Title */}
          <h2 className="text-2xl md:text-4xl font-black text-yellow-400 mb-4 md:mb-10 uppercase tracking-wide" data-id="element-186">
            El precio de lanzamiento termina en
          </h2>

          {/* Countdown boxes */}
          <div className="flex items-center justify-center gap-2 md:gap-5 mb-5 md:mb-10" data-id="element-187">
            <div className="flex flex-col items-center" data-id="element-188">
              <div className="bg-red-700 border-2 border-red-500 rounded-xl w-16 h-16 md:w-28 md:h-28 flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.4)]" data-id="element-189">
                <span className="text-white text-3xl md:text-6xl font-black" data-id="element-190">
                  {String(urgencyTime.hours).padStart(2, '0')}
                </span>
              </div>
              <span className="text-gray-500 text-xs md:text-sm font-bold uppercase tracking-wider mt-2" data-id="element-191">
                Horas
              </span>
            </div>
            <span className="text-red-500 text-3xl md:text-6xl font-black -mt-6" data-id="element-192">
              :
            </span>
            <div className="flex flex-col items-center" data-id="element-193">
              <div className="bg-red-700 border-2 border-red-500 rounded-xl w-16 h-16 md:w-28 md:h-28 flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.4)]" data-id="element-194">
                <span className="text-white text-3xl md:text-6xl font-black" data-id="element-195">
                  {String(urgencyTime.minutes).padStart(2, '0')}
                </span>
              </div>
              <span className="text-gray-500 text-xs md:text-sm font-bold uppercase tracking-wider mt-2" data-id="element-196">
                Minutos
              </span>
            </div>
            <span className="text-red-500 text-3xl md:text-6xl font-black -mt-6" data-id="element-197">
              :
            </span>
            <div className="flex flex-col items-center" data-id="element-198">
              <div className="bg-red-700 border-2 border-red-500 rounded-xl w-16 h-16 md:w-28 md:h-28 flex items-center justify-center shadow-[0_0_20px_rgba(220,38,38,0.4)]" data-id="element-199">
                <span className="text-white text-3xl md:text-6xl font-black" data-id="element-200">
                  {String(urgencyTime.seconds).padStart(2, '0')}
                </span>
              </div>
              <span className="text-gray-500 text-xs md:text-sm font-bold uppercase tracking-wider mt-2" data-id="element-201">
                Segundos
              </span>
            </div>
          </div>

          {/* Info badges */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-5 md:mb-10" data-id="element-202">
            <span className="flex items-center gap-2 bg-[#111633] border border-[#1e2a5a] text-gray-300 text-xs md:text-sm font-medium px-4 py-2.5 rounded-lg" data-id="element-203">
              🔥 Últimas unidades disponibles
            </span>
            <span className="flex items-center gap-2 bg-[#111633] border border-[#1e2a5a] text-gray-300 text-xs md:text-sm font-medium px-4 py-2.5 rounded-lg" data-id="element-204">
              📢 Oferta de Lanzamiento
            </span>
            <span className="flex items-center gap-2 bg-[#111633] border border-[#1e2a5a] text-gray-300 text-xs md:text-sm font-medium px-4 py-2.5 rounded-lg" data-id="element-205">
              📈 Precio sube al terminar
            </span>
          </div>

          {/* Progress bar */}
          <div className="max-w-lg mx-auto mb-5 md:mb-10" data-id="element-206">
            <div className="relative w-full h-5 bg-gray-800 rounded-full overflow-hidden border border-gray-700" data-id="element-207">
              <div className="h-full rounded-full" style={{
              width: '72%',
              background: 'linear-gradient(90deg, #f97316 0%, #ef4444 60%, #dc2626 100%)'
            }} data-id="element-208" />
            </div>
            <p className="text-gray-400 text-sm font-bold mt-3 uppercase tracking-wider" data-id="element-209">
              72 % de cupos ocupados
            </p>
          </div>

          {/* CTA */}
        </div>
      </section>

      {/* SECTION 9: PRICING */}
      <section id="pricing" className="py-8 md:py-20 px-4" style={{
      background: 'linear-gradient(180deg, #080c1a 0%, #0d1229 50%, #080c1a 100%)'
    }} data-id="element-210">
        <div className="max-w-5xl mx-auto" data-id="element-211">
          {/* Title */}
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-6 md:mb-12 italic" data-id="element-212">
            Elegí tu{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-8" data-id="element-213">
              Pack Completo
            </span>
          </h2>

          {/* Pack Cards - 2x2 Grid */}
          <div className="grid grid-cols-2 gap-2 md:gap-4 mb-4 md:mb-8" data-id="element-214">
            {/* Card 1: Kit Economía - $19.990 */}
            <button onClick={() => setSelectedPack(0)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 0 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-215">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 0 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-216">
                {selectedPack === 0 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-217" />}
              </div>
              <div className="text-xl mb-1" data-id="element-218">📚</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-219">
                Kit Completo{' '}
                <span className="text-[#22c55e]" data-id="element-220">Economía Lista</span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-221">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-222">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-223" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-224">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-225" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-226">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-227" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-228">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-229">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-230">
                $39.990
              </span>
            </button>

            {/* Card 2: Kit Contabilidad - $19.990 */}
            <button onClick={() => setSelectedPack(2)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 2 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-231">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 2 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-232">
                {selectedPack === 2 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-233" />}
              </div>
              <div className="text-xl mb-1" data-id="element-234">🧮</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-235">
                Kit Completo{' '}
                <span className="text-[#22c55e]" data-id="element-236">Contabilidad Lista</span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-237">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-238">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-239" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-240">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-241" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-242">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-243" />
                  3 bonus adaptados a Contabilidad
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-244">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-245">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-246">
                $39.990
              </span>
            </button>

            {/* Card 3: Kit Proyecto Organizacional - $19.990 */}
            <button onClick={() => setSelectedPack(4)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 4 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-247">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 4 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-248">
                {selectedPack === 4 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-249" />}
              </div>
              <div className="text-xl mb-1" data-id="element-250">📋</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-251">
                Kit Completo{' '}
                <span className="text-[#22c55e]" data-id="element-252">Proyecto Organizacional</span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-253">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-254">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-255" />
                  6 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-256">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-257" />
                  2 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-258">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-259">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-260">
                $39.990
              </span>
            </button>

            {/* Card 4: Kit Economía Completo + Sistema Pro - $27.990 */}
            <button onClick={() => setSelectedPack(1)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 1 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-261">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#22c55e] text-white text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-262">
                ⭐ Más elegido
              </span>
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 1 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-263">
                {selectedPack === 1 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-264" />}
              </div>
              <div className="text-xl mb-1" data-id="element-265">👑</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-266">
                Kit de Economía Completo +{' '}
                <span className="text-[#22c55e]" data-id="element-267">
                  Sistema Pro de Evaluación
                </span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-268">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-269">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-270" />
                  4 tomos teoría + 4 actividades (Economía)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-271">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-272" />
                  Sistema Pro de Evaluación incluido
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-273">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-274" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-275">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-276">
                  $27.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-277">
                $49.990
              </span>
            </button>

            {/* Card 5: Economía Lista + Teoría de las Organizaciones - $37.990 */}
            <button onClick={() => setSelectedPack(5)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 5 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-278">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 5 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-279">
                {selectedPack === 5 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-280" />}
              </div>
              <div className="text-xl mb-1" data-id="element-281">📖</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-282">
                Economía Lista +{' '}
                <span className="text-[#22c55e]" data-id="element-283">
                  Teoría de las Organizaciones
                </span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-284">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-285">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-286" />
                  10 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-287">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-288" />
                  6 bonus incluidos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-289">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-290" />
                  Todo el material completo
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-291">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-292">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-293">
                $69.990
              </span>
            </button>

            {/* Card 6: Pack Premium - $37.990 */}
            <button onClick={() => setSelectedPack(3)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 3 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-294">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-295">
                🏆 Pack Premium
              </span>
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 3 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-296">
                {selectedPack === 3 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-297" />}
              </div>
              <div className="text-xl mb-1" data-id="element-298">💎</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-299">
                Economía + Contabilidad +{' '}
                <span className="text-[#22c55e]" data-id="element-300">
                  Sistema Pro de Evaluación
                </span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-301">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-302">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-303" />
                  8 tomos teoría (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-304">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-305" />
                  8 tomos actividades (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-306">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-307" />
                  Sistema Pro de Evaluación incluido
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-308">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-309" />
                  Todos los bonus de ambos kits
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-310">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-311">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-312">
                $69.990
              </span>
            </button>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-4 md:mb-6" data-id="element-313">
            <a href={packLinks[selectedPack]} className="inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-lg md:text-2xl py-4 px-8 md:py-5 md:px-16 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] transform hover:scale-[1.02] transition-all duration-200" data-id="element-314">
              Sí, quiero mi pack →
            </a>
            <p className="text-gray-500 text-xs md:text-sm font-medium mt-3 flex items-center justify-center gap-1.5" data-id="element-315">
              <Lock className="w-3.5 h-3.5" data-id="element-316" /> PAGO SEGURO CON MERCADO PAGO
            </p>
          </div>

          {/* Features Box */}
          <div className="max-w-2xl mx-auto bg-[#111633] border border-[#1e2a5a] rounded-2xl p-4 md:p-8" data-id="element-317">
            <h3 className="text-white font-black text-base md:text-lg mb-3 md:mb-5 flex items-center gap-2" data-id="element-318">
              👑 PACK COMPLETO INCLUYE:
            </h3>
            <ul className="space-y-2 md:space-y-3" data-id="element-319">
              {['Kit completo de contenidos para todo el año', 'Maletín de actividades listas para el aula', 'BONUS: Guía docente de primeros auxilios', 'BONUS: Guía maestra de planificación anual', 'BONUS: Actividades para tus clases', 'Entrega inmediata por Drive (PDF)', 'Acceso de por vida — sin vencimiento', 'Garantía de satisfacción de 7 días'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm md:text-base" data-id="element-320">
                  <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-321" />
                  {item}
                </li>)}
            </ul>
          </div>

          {/* Payment methods */}
          <p className="text-center text-gray-600 text-xs md:text-sm mt-4 md:mt-6 flex items-center justify-center gap-2" data-id="element-322">
            <CreditCard className="w-4 h-4" data-id="element-323" />
            Transferencia · Mercado Pago · Tarjeta
          </p>
        </div>
      </section>

      {/* SECTION 10: HOW IT WORKS */}
      <section className="bg-white py-4 md:py-12 px-4" data-id="element-324">
        <div className="max-w-3xl mx-auto text-center" data-id="element-325">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-8" data-id="element-326">
            🔽 ¿Cómo recibís el{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-4" data-id="element-327">
              material
            </span>
            ?
          </h2>

          <div className="grid grid-cols-3 gap-2 md:gap-6" data-id="element-328">
            <div className="relative" data-id="element-329">
              <div className="w-9 h-9 md:w-14 md:h-14 bg-gray-100 rounded-full flex items-center justify-center text-base md:text-2xl font-bold text-gray-900 mx-auto mb-1 md:mb-3 border-2 border-gray-200" data-id="element-330">
                1
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2" data-id="element-331">
                Comprás
              </h3>
              <p className="text-gray-600 text-[11px] md:text-base leading-tight" data-id="element-332">
                Realizás la compra e ingresás tu correo.
              </p>
            </div>
            <div className="relative" data-id="element-333">
              <div className="w-9 h-9 md:w-14 md:h-14 bg-gray-100 rounded-full flex items-center justify-center text-base md:text-2xl font-bold text-gray-900 mx-auto mb-1 md:mb-3 border-2 border-gray-200" data-id="element-334">
                2
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2" data-id="element-335">
                Pagás
              </h3>
              <p className="text-gray-600 text-[11px] md:text-base leading-tight" data-id="element-336">
                Finalizás el pago de forma segura.
              </p>
            </div>
            <div className="relative" data-id="element-337">
              <div className="w-9 h-9 md:w-14 md:h-14 bg-gray-100 rounded-full flex items-center justify-center text-base md:text-2xl font-bold text-gray-900 mx-auto mb-1 md:mb-3 border-2 border-gray-200" data-id="element-338">
                3
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900 mb-0.5 md:mb-2" data-id="element-339">
                Descargás
              </h3>
              <p className="text-gray-600 text-[11px] md:text-base leading-tight" data-id="element-340">
                Recibís el link de descarga en tu correo.
              </p>
            </div>
          </div>

          <div className="mt-3 md:mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-2 md:p-4 inline-block" data-id="element-341">
            <p className="text-yellow-800 flex items-center justify-center gap-1.5 font-medium text-xs md:text-base" data-id="element-342">
              <AlertTriangle className="w-3.5 h-3.5 md:w-5 md:h-5" data-id="element-343" />
              Si no lo ves en tu bandeja, revisá spam.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 12: GUARANTEE */}
      <section className="bg-white py-8 md:py-16 px-4 border-t border-gray-100" data-id="element-344">
        <div className="max-w-3xl mx-auto text-center" data-id="element-345">
          <ShieldCheck className="w-14 h-14 md:w-20 md:h-20 text-[#22c55e] mx-auto mb-3 md:mb-6" data-id="element-346" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2 md:mb-4" data-id="element-347">
            Garantía de{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-4" data-id="element-348">
              Satisfacción Total
            </span>
          </h2>
          <p className="text-xl text-gray-600" data-id="element-349">
            Probalo 7 días. Si no cumple tus expectativas, te devolvemos el
            100%. Sin preguntas.
          </p>
        </div>
      </section>

      {/* SECTION 13: SOCIAL PROOF */}
      <section className="bg-gray-50 py-10 md:py-24 px-4" data-id="element-350">
        <div className="max-w-6xl mx-auto" data-id="element-351">
          {/* Header */}
          <div className="text-center mb-4 md:mb-6" data-id="element-352">
            <span className="inline-block bg-[#22c55e]/10 text-[#22c55e] text-xs md:text-sm font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3 md:mb-4" data-id="element-353">
              Prueba Social
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-3 md:mb-4" data-id="element-354">
              Lo que dicen{' '}
              <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-4" data-id="element-355">
                docentes reales
              </span>
            </h2>
            <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-356">
              Más de 500 docentes de toda Argentina ya transformaron su forma de
              enseñar Economía.
            </p>
          </div>

          {/* Stats bar */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-8 md:mb-14" data-id="element-357">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm" data-id="element-358">
              <span className="text-[#22c55e] font-black text-lg md:text-xl" data-id="element-359">
                +500
              </span>
              <span className="text-gray-600 text-xs md:text-sm font-medium" data-id="element-360">
                Docentes confían
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm" data-id="element-361">
              <span className="text-[#22c55e] font-black text-lg md:text-xl" data-id="element-362">
                4.9
              </span>
              <div className="flex gap-0.5 text-yellow-400" data-id="element-363">
                {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3.5 h-3.5 fill-current" data-id="element-364" />)}
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm" data-id="element-365">
              <span className="text-[#22c55e] font-black text-lg md:text-xl" data-id="element-366">
                23
              </span>
              <span className="text-gray-600 text-xs md:text-sm font-medium" data-id="element-367">
                Provincias
              </span>
            </div>
          </div>

          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-368">
            {[{
            text: 'Ahora organizo todo el año sin estrés. Es increíble el tiempo que ahorro. Antes pasaba fines de semana enteros armando clases.',
            author: 'Natalia Gutiérrez',
            role: 'Prof. de Economía',
            loc: 'Córdoba',
            img: 'https://randomuser.me/api/portraits/women/44.jpg'
          }, {
            text: 'Mis estudiantes entienden la inflación y las crisis con ejemplos reales. Las actividades son excelentes y muy bien pensadas.',
            author: 'Martín Rodríguez',
            role: 'Prof. de Economía y Contabilidad',
            loc: 'Buenos Aires',
            img: 'https://randomuser.me/api/portraits/men/32.jpg'
          }, {
            text: 'Las clases cambiaron por completo. Todo está más claro y estructurado. Mis alumnos participan mucho más.',
            author: 'Laura Pereyra',
            role: 'Prof. de Economía',
            loc: 'Mendoza',
            img: 'https://randomuser.me/api/portraits/women/68.jpg'
          }, {
            text: 'Antes tardaba horas buscando material. Ahora tengo todo listo y puedo dedicar ese tiempo a mi familia.',
            author: 'Cecilia Morales',
            role: 'Prof. de Economía',
            loc: 'Rosario',
            img: 'https://randomuser.me/api/portraits/women/65.jpg'
          }, {
            text: 'Las simulaciones de mercado hacen que participen todos. Es el mejor recurso que compré en años.',
            author: 'Diego Fernández',
            role: 'Prof. de Economía y Gestión',
            loc: 'Tucumán',
            img: 'https://randomuser.me/api/portraits/men/75.jpg'
          }, {
            text: 'Lo recomendé a todo mi departamento. La calidad del material es profesional y está perfectamente adaptado al currículo.',
            author: 'Valeria Sánchez',
            role: 'Jefa de Depto. Economía',
            loc: 'Santa Fe',
            img: 'https://randomuser.me/api/portraits/women/47.jpg'
          }].map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-5 md:p-6 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.04)] border border-gray-100 flex flex-col" data-id="element-369">
                {/* Stars */}
                <div className="flex gap-0.5 text-yellow-400 mb-3" data-id="element-370">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-current" data-id="element-371" />)}
                </div>

                {/* Quote */}
                <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-4 flex-1" data-id="element-372">
                  "{t.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100" data-id="element-373">
                  <img src={t.img} alt={t.author} className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover ring-2 ring-gray-100" data-id="element-374" />
                  <div data-id="element-375">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-376">
                      {t.author}
                    </p>
                    <p className="text-gray-500 text-xs" data-id="element-377">
                      {t.role} · {t.loc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 14: FAQ */}
      <section className="bg-white py-8 md:py-16 px-4" data-id="element-378">
        <div className="max-w-3xl mx-auto" data-id="element-379">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-6 md:mb-12" data-id="element-380">
            Preguntas{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-4" data-id="element-381">
              Frecuentes
            </span>
          </h2>

          <div className="space-y-2 md:space-y-4" data-id="element-382">
            {[{
            q: '¿En qué formato recibo el material?',
            a: 'Recibís todo en formato PDF, listo para descargar e imprimir o usar en formato digital.'
          }, {
            q: '¿Puedo imprimir el material?',
            a: 'Sí, podés imprimir todo el material las veces que necesites.'
          }, {
            q: '¿Para qué año sirve?',
            a: 'El kit está diseñado para todos los años de secundaria donde se dicte Economía.'
          }, {
            q: '¿Necesito materiales especiales?',
            a: 'No, solo necesitás una computadora o celular para descargar el material.'
          }, {
            q: '¿Cuánto tarda en llegar?',
            a: 'El acceso es inmediato. Apenas completás el pago, recibís el link de descarga en tu correo.'
          }].map((faq, i) => <details key={i} className="group bg-gray-50 rounded-lg border border-gray-200 open:bg-white open:shadow-md transition-all duration-200" data-id="element-383">
                <summary className="flex items-center justify-between cursor-pointer p-4 md:p-6 font-bold text-gray-900 list-none" data-id="element-384">
                  {faq.q}
                  <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" data-id="element-385" />
                </summary>
                <div className="px-4 pb-4 md:px-6 md:pb-6 text-gray-600 leading-relaxed border-t border-gray-100 pt-4" data-id="element-386">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* SECTION 15: FINAL CTA */}
      <section className="bg-black text-white py-8 md:py-20 px-4 text-center" data-id="element-387">
        <div className="max-w-4xl mx-auto" data-id="element-388">
          <h2 className="text-3xl md:text-5xl font-black mb-2 md:mb-4" data-id="element-389">
            ¿Listo/a para{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-8" data-id="element-390">
              transformar tu año
            </span>{' '}
            en Economía?
          </h2>
          <p className="text-xl text-gray-400 mb-5 md:mb-10" data-id="element-391">
            Sumate a docentes que ya dejaron de improvisar.
          </p>

          <p className="text-gray-500 font-bold uppercase tracking-widest text-sm" data-id="element-392">
            📈 KIT COMPLETO ECONOMÍA LISTA
          </p>
        </div>
      </section>

      {/* SECTION 15.5: BOTTOM PRICING (duplicate) */}
      <section className="py-8 md:py-20 px-4" style={{
      background: 'linear-gradient(180deg, #080c1a 0%, #0d1229 50%, #080c1a 100%)'
    }} data-id="element-393">
        <div className="max-w-5xl mx-auto" data-id="element-394">
          <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-6 md:mb-12 italic" data-id="element-395">
            Elegí tu{' '}
            <span className="text-[#22c55e] underline decoration-[#22c55e]/30 decoration-4 underline-offset-8" data-id="element-396">
              Pack Completo
            </span>
          </h2>

          <div className="grid grid-cols-2 gap-2 md:gap-4 mb-4 md:mb-8" data-id="element-397">
            {/* Card 1: Kit Economía */}
            <button onClick={() => setSelectedPack(0)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 0 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-398">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 0 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-399">
                {selectedPack === 0 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-400" />}
              </div>
              <div className="text-xl mb-1" data-id="element-401">📚</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-402">
                Kit Completo{' '}
                <span className="text-[#22c55e]" data-id="element-403">Economía Lista</span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-404">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-405">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-406" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-407">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-408" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-409">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-410" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-411">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-412">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-413">
                $39.990
              </span>
            </button>

            {/* Card 2: Kit Contabilidad */}
            <button onClick={() => setSelectedPack(2)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 2 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-414">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 2 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-415">
                {selectedPack === 2 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-416" />}
              </div>
              <div className="text-xl mb-1" data-id="element-417">🧮</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-418">
                Kit Completo{' '}
                <span className="text-[#22c55e]" data-id="element-419">Contabilidad Lista</span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-420">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-421">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-422" />
                  4 tomos de teoría completa
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-423">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-424" />
                  4 tomos de actividades prácticas
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-425">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-426" />
                  3 bonus adaptados a Contabilidad
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-427">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-428">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-429">
                $39.990
              </span>
            </button>

            {/* Card 3: Kit Proyecto Organizacional */}
            <button onClick={() => setSelectedPack(4)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 4 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-430">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 4 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-431">
                {selectedPack === 4 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-432" />}
              </div>
              <div className="text-xl mb-1" data-id="element-433">📋</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-434">
                Kit Completo{' '}
                <span className="text-[#22c55e]" data-id="element-435">Proyecto Organizacional</span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-436">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-437">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-438" />
                  6 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-439">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-440" />
                  2 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-441">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-442">
                  $19.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-443">
                $39.990
              </span>
            </button>

            {/* Card 4: Kit Economía Completo + Sistema Pro */}
            <button onClick={() => setSelectedPack(1)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 1 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-444">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#22c55e] text-white text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-445">
                ⭐ Más elegido
              </span>
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 1 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-446">
                {selectedPack === 1 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-447" />}
              </div>
              <div className="text-xl mb-1" data-id="element-448">👑</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-449">
                Kit de Economía Completo +{' '}
                <span className="text-[#22c55e]" data-id="element-450">
                  Sistema Pro de Evaluación
                </span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-451">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-452">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-453" />
                  4 tomos teoría + 4 actividades (Economía)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-454">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-455" />
                  Sistema Pro de Evaluación incluido
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-456">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-457" />
                  3 bonus incluidos
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-458">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-459">
                  $27.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-460">
                $49.990
              </span>
            </button>

            {/* Card 5: Economía Lista + Teoría de las Organizaciones */}
            <button onClick={() => setSelectedPack(5)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 5 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-461">
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 5 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-462">
                {selectedPack === 5 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-463" />}
              </div>
              <div className="text-xl mb-1" data-id="element-464">📖</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-465">
                Economía Lista +{' '}
                <span className="text-[#22c55e]" data-id="element-466">
                  Teoría de las Organizaciones
                </span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-467">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-468">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-469" />
                  10 tomos completos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-470">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-471" />
                  6 bonus incluidos
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-472">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-473" />
                  Todo el material completo
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-474">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-475">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-476">
                $69.990
              </span>
            </button>

            {/* Card 6: Pack Premium */}
            <button onClick={() => setSelectedPack(3)} className={`relative text-left rounded-xl p-3 md:p-5 transition-all duration-200 cursor-pointer ${selectedPack === 3 ? 'bg-[#111633] border-2 border-[#22c55e] shadow-[0_0_20px_rgba(34,197,94,0.15)]' : 'bg-[#0d1025] border-2 border-[#1e2a5a] hover:border-[#2a3a6a]'}`} data-id="element-477">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[9px] md:text-xs font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap shadow-lg" data-id="element-478">
                🏆 Pack Premium
              </span>
              <div className={`absolute top-2.5 right-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center ${selectedPack === 3 ? 'border-[#22c55e] bg-[#22c55e]' : 'border-gray-600'}`} data-id="element-479">
                {selectedPack === 3 && <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" data-id="element-480" />}
              </div>
              <div className="text-xl mb-1" data-id="element-481">💎</div>
              <h3 className="text-sm md:text-lg font-black text-white mb-0.5 pr-5" data-id="element-482">
                Economía + Contabilidad +{' '}
                <span className="text-[#22c55e]" data-id="element-483">
                  Sistema Pro de Evaluación
                </span>
              </h3>
              <ul className="space-y-1 mb-2" data-id="element-484">
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-485">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-486" />
                  8 tomos teoría (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-487">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-488" />
                  8 tomos actividades (4 Eco + 4 Conta)
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-489">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-490" />
                  Sistema Pro de Evaluación incluido
                </li>
                <li className="flex items-start gap-1.5 text-gray-400 text-[10px] md:text-xs" data-id="element-491">
                  <Check className="w-3 h-3 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-492" />
                  Todos los bonus de ambos kits
                </li>
              </ul>
              <div className="flex items-baseline gap-1" data-id="element-493">
                <span className="text-2xl md:text-4xl font-black text-white" data-id="element-494">
                  $37.990
                </span>
              </div>
              <span className="text-gray-600 text-xs line-through" data-id="element-495">
                $69.990
              </span>
            </button>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-4 md:mb-6" data-id="element-496">
            <a href={packLinks[selectedPack]} className="inline-flex items-center justify-center gap-2 w-full md:w-auto bg-[#22c55e] hover:bg-[#16a34a] text-white font-black text-lg md:text-2xl py-4 px-8 md:py-5 md:px-16 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] transform hover:scale-[1.02] transition-all duration-200" data-id="element-497">
              Sí, quiero mi pack →
            </a>
            <p className="text-gray-500 text-xs md:text-sm font-medium mt-3 flex items-center justify-center gap-1.5" data-id="element-498">
              <Lock className="w-3.5 h-3.5" data-id="element-499" /> PAGO SEGURO CON MERCADO PAGO
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 16: FOOTER */}
      <footer className="bg-gray-900 text-gray-500 py-4 md:py-8 px-4 text-center text-sm border-t border-gray-800" data-id="element-500">
        <p className="mb-2 md:mb-4" data-id="element-501">© 2026 Kit Completo Economía Lista™</p>
        <div className="flex justify-center gap-4" data-id="element-502">
          <span className="hover:text-white cursor-pointer" data-id="element-503">Términos</span>
          <span className="hover:text-white cursor-pointer" data-id="element-504">Privacidad</span>
          <span className="hover:text-white cursor-pointer" data-id="element-505">Soporte</span>
        </div>
      </footer>
    </div>;
}